import React from 'react';

const BenefitsSection: React.FC = () => {
  const benefits = [
    {
      icon: 'ri-time-line',
      title: '24-Hour Guarantee',
      description: 'Connect with a board-certified physician within 24 hours, guaranteed. No more waiting weeks for appointments.',
      color: 'bg-blue-100 text-blue-600'
    },
    {
      icon: 'ri-infinite-line',
      title: 'Unlimited Consultations',
      description: 'No limits on virtual visits. Schedule as many consultations as you need throughout your subscription.',
      color: 'bg-teal-100 text-teal-600'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Comprehensive Care',
      description: 'Full primary care services including preventive care, chronic disease management, and acute illness treatment.',
      color: 'bg-green-100 text-green-600'
    },
    {
      icon: 'ri-medicine-bottle-line',
      title: 'Prescription Management',
      description: 'Electronic prescriptions sent directly to your pharmacy. Medication reviews and refill management included.',
      color: 'bg-purple-100 text-purple-600'
    },
    {
      icon: 'ri-file-text-line',
      title: 'Health Records Access',
      description: 'Secure access to all your medical records, test results, and treatment plans through our patient portal.',
      color: 'bg-orange-100 text-orange-600'
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Wellness Monitoring',
      description: 'Proactive health monitoring with personalized wellness plans and preventive care recommendations.',
      color: 'bg-red-100 text-red-600'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Why Choose Virtual Primary Care?
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Experience healthcare the way it should be - accessible, comprehensive, and centered around your needs.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <div key={index} className="group hover:shadow-lg transition-all duration-300 p-8 rounded-xl border border-gray-100 hover:border-teal-200">
              <div className={`w-16 h-16 ${benefit.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <i className={`${benefit.icon} text-2xl`}></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {benefit.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-16 bg-gradient-to-r from-teal-50 to-blue-50 rounded-2xl p-8 lg:p-12">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">
                Enhanced Service Perks
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <i className="ri-check-line text-teal-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Priority Scheduling:</strong>
                    <span className="text-gray-600 ml-1">Skip the wait with priority access to all our specialty services</span>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-check-line text-teal-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Discounted Lab Work:</strong>
                    <span className="text-gray-600 ml-1">20% off all laboratory tests and diagnostic procedures</span>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-check-line text-teal-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Specialist Referrals:</strong>
                    <span className="text-gray-600 ml-1">Direct referrals to our network of trusted specialists</span>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-check-line text-teal-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Family Coverage:</strong>
                    <span className="text-gray-600 ml-1">Add family members at discounted rates</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://public.readdy.ai/ai/img_res/c8ec5054559c05e2beb47249dae2d52e.jpg"
                alt="Family using virtual care"
                className="w-full h-auto rounded-xl shadow-lg object-cover"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;